import { TypedDocumentNode, gql } from '@apollo/client';
import { faExclamationCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import {
  CardQuality,
  DivisionChange,
  FiatCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Card } from '@sorare/core/src/atoms/icons/Card';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { StatDisplay } from '@sorare/core/src/atoms/ui/StatDisplay';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { qualityNames } from '@sorare/core/src/lib/players';
import { BoxSimpleReward, RewardType } from '@sorare/core/src/lib/rewards';
import { RANKED_SCARCITY } from '@sorare/core/src/lib/scarcity';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CompactContender } from 'components/contender/CompactContender';
import { ClickableRewardOverview } from 'components/rewards/ClickableRewardOverview';
import { RewardOverview } from 'components/rewards/RewardOverview';
import { Variant } from 'components/rewards/RewardOverview/types';
import { useSo5RewardOrConfigSimpleRewards } from 'hooks/so5/useSo5RewardOrConfigSimpleRewards';

import { FixtureOverviewTitle } from './FixtureOverviewTitle';
import {
  FixtureOverview_so5Fixture,
  FixtureOverview_so5RewardOrConfig,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  @media ${laptopAndAbove} {
    min-width: 420px;
  }
`;
const Counts = styled(Horizontal)`
  justify-content: space-evenly;

  > * {
    flex-basis: 100px;
    min-width: 0;
  }
`;
const RigidSquareLabelL = styled(LabelL)`
  font-family: var(--rigid-square);
`;

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;
const Block = styled.div`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;
const WrapHorizontal = styled(Horizontal)`
  flex-wrap: wrap;
  max-height: 135px;
  overflow-y: auto;
`;
const DivisionChanges = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--quadruple-unit);
`;
const Progress = styled.div`
  width: 100%;
  height: var(--unit);
  border-radius: var(--unit);
  background: var(--bg-color);
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: calc(100% - var(--progress));
    background: var(--progress-color);
    border-radius: var(--double-unit);
  }
`;

type Props = {
  so5Fixture?: FixtureOverview_so5Fixture;
  so5Rewards?: FixtureOverview_so5RewardOrConfig[];
  onClose?: () => void;
  scrollToContender?: (contender: string) => void;
  onlyRewards?: boolean;
  verificationNeeded?: { phoneNumber?: boolean; kyc?: boolean };
};
export const FixtureOverview = ({
  so5Fixture,
  so5Rewards,
  onClose,
  scrollToContender,
  onlyRewards,
  verificationNeeded,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { formatNumber } = useIntl();

  const { myEligibleOrSo5Rewards, mySo5Lineups, first10So5Rankings } =
    so5Fixture || {};

  const { simpleRewards } = useSo5RewardOrConfigSimpleRewards({
    so5RewardOrConfigs: so5Rewards || myEligibleOrSo5Rewards || [],
    variant: Variant.INLINE,
    rarity: null,
  });

  const {
    PROBABILISTIC_BUNDLE: boxRewards = [],
    CARD: cardRewards = [],
    CASH: cashRewards = [],
    COIN: coinRewards = [],
    CREDIT: conversionCreditRewards = [],
    LEVEL_UP: levelUpRewards = [],
    SHARD: shardRewards = [],
  } = groupBy(simpleRewards, ({ simpleReward }) => simpleReward.type);

  const totalCash = cashRewards.reduce(
    (acc, { simpleReward }) => acc + simpleReward.count,
    0
  );

  const formattedTotalCash = formatNumber(totalCash, {
    style: 'currency',
    currency: FiatCurrency.USD,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });
  const sortedBoxRewards = boxRewards.sort((a, b) => {
    const simpleRewardA = a.simpleReward as BoxSimpleReward;
    const simpleRewardB = b.simpleReward as BoxSimpleReward;

    const scarcityCompare =
      (simpleRewardB.rarity ? RANKED_SCARCITY[simpleRewardB.rarity] : 0) -
      (simpleRewardA.rarity ? RANKED_SCARCITY[simpleRewardA.rarity] : 0);
    const starCompare =
      (simpleRewardB.boxConfig?.stars || 0) -
      (simpleRewardA.boxConfig?.stars || 0);

    return scarcityCompare || starCompare;
  });

  const cardRewardsCountPerRarityPerQuality = cardRewards.reduce(
    (acc, reward) => {
      const { rarity, quality } = reward.simpleReward;
      if (!rarity || !quality) {
        return acc;
      }

      return {
        ...acc,
        [rarity]: {
          ...acc[rarity],
          count: (acc[rarity]?.count || 0) + 1,
          [quality]: (acc[rarity]?.[quality] || 0) + 1,
        },
      };
    },
    {} as Record<Rarity, { count: number } & Record<CardQuality, number>>
  );

  const eligibleDivisionChanges = (mySo5Lineups || [])
    .flatMap(so5Lineup => so5Lineup.so5Rankings.at(0)?.eligibleDivisionChange)
    .filter(Boolean);

  const divisionChangesPerChange = groupBy(
    eligibleDivisionChanges,
    change => change
  );

  const topContenders = (first10So5Rankings || []).map(
    ({ so5Lineup }) => so5Lineup.so5LeaderboardContender
  );

  const mainRewardsPresent =
    boxRewards.length > 0 || cardRewards.length > 0 || cashRewards.length > 0;
  const fancyRewardsPresent =
    coinRewards.length > 0 ||
    conversionCreditRewards.length > 0 ||
    levelUpRewards.length > 0 ||
    shardRewards.length > 0;

  return (
    <Root gap={6}>
      {!isLaptopAndAbove && onClose && (
        <StyledIconButton
          icon={faTimes}
          color="transparent"
          smaller
          onClick={onClose}
        />
      )}
      {!onlyRewards && so5Fixture && (
        <Vertical center gap={2.5}>
          <StarBall width={40} height={40} />
          <RigidSquareLabelL bold uppercase color="var(--c-nd-600)">
            <FixtureOverviewTitle so5Fixture={so5Fixture} />
          </RigidSquareLabelL>
        </Vertical>
      )}
      {mainRewardsPresent && (
        <Counts>
          {boxRewards.length > 0 && (
            <StatDisplay
              size={56}
              icon={<Box size={4} gradient="red" />}
              label={
                <Horizontal>
                  {verificationNeeded?.phoneNumber && (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="var(--c-yellow-400)"
                    />
                  )}
                  <BodyS
                    color={
                      verificationNeeded?.phoneNumber
                        ? 'var(--c-yellow-400)'
                        : undefined
                    }
                  >
                    <FormattedMessage {...fantasy.boxes} />
                  </BodyS>
                </Horizontal>
              }
            >
              {boxRewards.length}
            </StatDisplay>
          )}
          {cardRewards.length > 0 && (
            <StatDisplay
              size={56}
              icon={<Card size={4} gradient="blue" />}
              label={
                <Horizontal>
                  {verificationNeeded?.phoneNumber && (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="var(--c-yellow-400)"
                    />
                  )}
                  <BodyS
                    color={
                      verificationNeeded?.phoneNumber
                        ? 'var(--c-yellow-400)'
                        : undefined
                    }
                  >
                    <FormattedMessage {...glossary.cards} />
                  </BodyS>
                </Horizontal>
              }
            >
              {cardRewards.length}
            </StatDisplay>
          )}
          {cashRewards.length > 0 && (
            <StatDisplay
              label={
                <Horizontal>
                  {verificationNeeded?.kyc && (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      color="var(--c-yellow-400)"
                    />
                  )}
                  <BodyS
                    color={
                      verificationNeeded?.kyc
                        ? 'var(--c-yellow-400)'
                        : undefined
                    }
                  >
                    <FormattedMessage {...glossary.cash} />
                  </BodyS>
                </Horizontal>
              }
            >
              {formattedTotalCash}
            </StatDisplay>
          )}
        </Counts>
      )}
      <Vertical gap={3}>
        {boxRewards.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage {...fantasy.boxes} />
            </LabelM>
            <Block>
              <WrapHorizontal>
                {sortedBoxRewards.map(
                  (
                    {
                      simpleReward,
                      probabilisticBundleConfigs,
                      deliverableItems,
                      experiences,
                    },
                    i
                  ) => (
                    <ClickableRewardOverview
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      simpleReward={simpleReward}
                      variant={Variant.CLASSIC}
                      size={5}
                      probabilisticBundleConfigs={probabilisticBundleConfigs}
                      deliverableItems={deliverableItems}
                      experiences={experiences}
                    />
                  )
                )}
              </WrapHorizontal>
            </Block>
          </Vertical>
        )}
        {cardRewards.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage {...glossary.cards} />
            </LabelM>
            <Block>
              <Vertical gap={2}>
                {Object.entries(cardRewardsCountPerRarityPerQuality).map(
                  ([rarity, { count, ...qualities }]) => (
                    <WrapHorizontal key={rarity}>
                      <RewardOverview
                        variant={Variant.INLINE}
                        simpleReward={{
                          type: RewardType.CARD,
                          rarity: rarity as Rarity,
                          count,
                        }}
                      />
                      {Object.entries(qualities).map(
                        ([quality, qualityCount]) => (
                          <Chip key={quality}>
                            <Horizontal gap={0.5}>
                              <LabelM bold color="var(--c-white)">
                                <FormattedNumber value={qualityCount} />
                              </LabelM>
                              <LabelM color="var(--c-nd-600)">
                                {qualityNames[quality as CardQuality]}
                              </LabelM>
                            </Horizontal>
                          </Chip>
                        )
                      )}
                    </WrapHorizontal>
                  )
                )}
              </Vertical>
            </Block>
          </Vertical>
        )}
        {onlyRewards ? (
          <>
            {fancyRewardsPresent && (
              <Vertical gap={1.5}>
                <LabelM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="FixtureOverview.specials"
                    defaultMessage="Specials"
                  />
                </LabelM>
                <Block>
                  <WrapHorizontal>
                    {[
                      ...coinRewards,
                      ...conversionCreditRewards,
                      ...levelUpRewards,
                      ...shardRewards,
                    ].map(
                      (
                        {
                          simpleReward,
                          experiences,
                          probabilisticBundleConfigs,
                          deliverableItems,
                        },
                        i
                      ) => (
                        <ClickableRewardOverview
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          simpleReward={simpleReward}
                          variant={Variant.CLASSIC}
                          size={5}
                          experiences={experiences}
                          probabilisticBundleConfigs={
                            probabilisticBundleConfigs
                          }
                          deliverableItems={deliverableItems}
                        />
                      )
                    )}
                  </WrapHorizontal>
                </Block>
              </Vertical>
            )}
          </>
        ) : (
          <>
            {eligibleDivisionChanges.length > 0 && (
              <Vertical gap={1.5}>
                <LabelM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="FixtureOverview.overallPerformance"
                    defaultMessage="Overall Performance"
                  />
                </LabelM>
                <Block>
                  <DivisionChanges>
                    <Vertical center>
                      <LabelS>
                        <FormattedMessage {...fantasy.promotion} />
                      </LabelS>
                      <Progress
                        style={
                          {
                            '--bg-color': 'rgba(var(--c-rgb-rivals-win), .2)',
                            '--progress-color': 'var(--c-rivals-win)',
                            '--progress': `${
                              ((divisionChangesPerChange[
                                DivisionChange.PROMOTED
                              ]?.length || 0) /
                                eligibleDivisionChanges.length) *
                              100
                            }%`,
                          } as CSSProperties
                        }
                      />
                      <LabelS color="var(--c-rivals-win)">
                        <FormattedNumber
                          value={
                            divisionChangesPerChange[DivisionChange.PROMOTED]
                              ?.length || 0
                          }
                        />
                      </LabelS>
                    </Vertical>
                    <Vertical center>
                      <LabelS>
                        <FormattedMessage
                          id="FixtureOverview.safety"
                          defaultMessage="Safety"
                        />
                      </LabelS>
                      <Progress
                        style={
                          {
                            '--bg-color': 'rgba(var(--c-rgb-white), .1)',
                            '--progress-color': 'var(--c-white)',
                            '--progress': `${
                              ((divisionChangesPerChange[DivisionChange.STABLE]
                                ?.length || 0) /
                                eligibleDivisionChanges.length) *
                              100
                            }%`,
                          } as CSSProperties
                        }
                      />
                      <LabelS>
                        <FormattedNumber
                          value={
                            divisionChangesPerChange[DivisionChange.STABLE]
                              ?.length || 0
                          }
                        />
                      </LabelS>
                    </Vertical>
                    <Vertical center>
                      <LabelS>
                        <FormattedMessage {...fantasy.relegation} />
                      </LabelS>
                      <Progress
                        style={
                          {
                            '--bg-color': 'rgba(var(--c-rgb-rivals-loss), .2)',
                            '--progress-color': 'var(--c-rivals-loss)',
                            '--progress': `${
                              ((divisionChangesPerChange[
                                DivisionChange.RELEGATED
                              ]?.length || 0) /
                                eligibleDivisionChanges.length) *
                              100
                            }%`,
                          } as CSSProperties
                        }
                      />
                      <LabelS color="var(--c-rivals-loss)">
                        <FormattedNumber
                          value={
                            divisionChangesPerChange[DivisionChange.RELEGATED]
                              ?.length || 0
                          }
                        />
                      </LabelS>
                    </Vertical>
                  </DivisionChanges>
                </Block>
              </Vertical>
            )}
            {topContenders.length > 0 && (
              <Vertical gap={1.5}>
                <LabelM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="FixtureOverview.topRankingTeams"
                    defaultMessage="Top Ranking Teams"
                  />
                </LabelM>
                <Vertical gap={2}>
                  {topContenders.map(contender => (
                    <CompactContender
                      key={contender.slug}
                      so5LeaderboardContender={contender}
                      onClick={() => {
                        scrollToContender?.(contender.slug);
                        onClose?.();
                      }}
                    />
                  ))}
                </Vertical>
              </Vertical>
            )}
          </>
        )}
      </Vertical>
    </Root>
  );
};

const so5RewardOrConfigFragment = gql`
  fragment FixtureOverview_so5RewardOrConfig on So5RewardOrConfig {
    ...useSo5RewardOrConfigSimpleRewards_so5RewardOrConfig
  }
  ${useSo5RewardOrConfigSimpleRewards.fragments.so5RewardOrConfig}
` as TypedDocumentNode<FixtureOverview_so5RewardOrConfig>;

FixtureOverview.fragments = {
  so5RewardOrConfig: so5RewardOrConfigFragment,
  so5Fixture: gql`
    fragment FixtureOverview_so5Fixture on So5Fixture {
      slug
      first10So5Rankings: mySo5Rankings(first: 10, withTraining: false) {
        id
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
            ...CompactContender_so5LeaderboardContender
          }
        }
      }
      myEligibleOrSo5Rewards {
        ...FixtureOverview_so5RewardOrConfig
      }
      mySo5Lineups {
        id
        so5Rankings {
          id
          eligibleDivisionChange
        }
      }
      ...FixtureOverviewTitle_so5Fixture
    }
    ${so5RewardOrConfigFragment}
    ${CompactContender.fragments.so5LeaderboardContender}
    ${FixtureOverviewTitle.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureOverview_so5Fixture>,
};
